import { getUnixTime } from 'date-fns';

const baseUrl = import.meta.env.VITE_APP_BASE_URL;
const railsBaseUrl = import.meta.env.VITE_RAILS_BASE_URL;
const v3BaseUrl = import.meta.env.VITE_V3_BASE_URL;
const exerciseBaseUrl = import.meta.env.VITE_EXERCISE_BASE_URL;
const ngrokUrl = import.meta.env.VITE_APP_CONNECT_URL;

const makeAbsolute = (url: string): string => {
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url.startsWith('/') ? url : `/${url}`}`;
};

export const externalLinks: any = {
  base: `${baseUrl}`,
  railsBase: `${railsBaseUrl}`,
  home: `${railsBaseUrl}/`,
  trainerDashboard: `${railsBaseUrl}/dashboard/trainer-dashboard/`,
  notifications: `${baseUrl}/me/notifications/`,
  billing: `${baseUrl}/me/account/`,
  support: `${baseUrl}/support/`,
  signOut: `${railsBaseUrl}/users/sign_out/`,
  login: `${baseUrl}/login`,
  connectedApps: `${baseUrl}/me/apps/`,
  loggedWorkouts: `${baseUrl}/reports/live/workouts`,
  activity: `${baseUrl}/activity/`,
  liveReports: `${baseUrl}/reports/?page=1&search=Real-Time`,
  measurements: `${baseUrl}/me/measurements/`,
  schedule: `${baseUrl}/calendar/`,
  messages: `${baseUrl}/messages/`,
  workouts: `${baseUrl}/reports/live/workouts`,
  subscriptions: `${baseUrl}/reports/live/subscriptions`,
  cart: `${v3BaseUrl}/checkout/`,
  ask: `${railsBaseUrl}/dashboard/ask/`,
  manageLifecycle: `${baseUrl}/lifecycle`,
  absolute: makeAbsolute,

  v3Url: (url: string): string => `${v3BaseUrl}${url}`,
  v2Url: (url: string): string => `${railsBaseUrl}${url}`,

  platformAdmin: (platformId?: string): string => {
    const mode = import.meta.env.MODE;
    const id = platformId ? `${platformId}/details` : '';
    if (mode === 'development') {
      return `http://localhost:5001/ex4/admin/platform/${id}/`;
    } else if (mode === 'staging') {
      return `https://staging.exercise.com/ex4/admin/platform/${id}/`;
    } else {
      return `https://www.exercise.com/ex4/admin/platform/${id}/`;
    }
  },

  accountManager: (): string => `${baseUrl}/account-manager`,
  searchUsers: (trainerId?: string | number): string =>
    `${baseUrl}/admin/user?f_trainerId=${trainerId ?? ''}`,
  newLifecycleStage: (): string => `${baseUrl}/lifecycle/new`,
  editLifecycleStage: (lifecycleStageId: string | number): string =>
    `${baseUrl}/lifecycle/${lifecycleStageId}`,

  viewExercise: (exerciseId: string | number): string =>
    `${railsBaseUrl}/exercises/${exerciseId}`,
  editExercise: (exerciseId: string | number): string =>
    `${railsBaseUrl}/dashboard/exercises/${exerciseId}`,
  newExercise: (): string => `${railsBaseUrl}/dashboard/exercises/new`,
  exercise: (id?: string | number): string =>
    `${railsBaseUrl}/exercises/${id || ''}`,

  v2EditRoutine: (routineId: string | number): string =>
    `${railsBaseUrl}/v2/routine/${routineId}/edit/`,

  newSchedule: (): string => `${baseUrl}/embed/fbm/schedules/new`,
  editSchedule: (scheduleId: string | number): string =>
    `${baseUrl}/embed/fbm/schedules/${scheduleId}`,
  fbmSchedules: (): string => `${railsBaseUrl}/fbm/schedules`,

  viewGroup: (cachedSlug?: string, id?: string | number): string =>
    `${baseUrl}/groups/${cachedSlug || id}/public`,
  newGroup: (): string => `${baseUrl}/groups/new`,
  viewGroups: (): string => `${baseUrl}/groups`,
  editGroup: (groupId: string | number): string =>
    `${railsBaseUrl}/dashboard/groups/${groupId}`,
  viewGroupVideos: (groupId: string | number): string =>
    `${railsBaseUrl}/dashboard/groups/${groupId}/videos`,
  viewGroupResources: (groupId: string | number): string =>
    `${railsBaseUrl}/dashboard/groups/${groupId}/resources`,
  viewGroupAssignments: (groupId: string | number): string =>
    `${railsBaseUrl}/dashboard/groups/${groupId}/assessments`,
  viewGroupCalendar: (groupId: string | number): string =>
    `${v3BaseUrl}/calendar/group/${groupId}`,
  manageGroupVideos: (groupId: string | number): string =>
    `${railsBaseUrl}/dashboard/groups/${groupId}/videos`,
  manageGroupResources: (groupId: string | number): string =>
    `${railsBaseUrl}/dashboard/groups/${groupId}/resources`,
  manageGroupAssignments: (groupId: string | number): string =>
    `${railsBaseUrl}/dashboard/groups/${groupId}/assessments`,
  landingPage: (trainerId: string | number): string =>
    `${baseUrl}/landing/${trainerId}`,
  finder: `${baseUrl}/purchase/`,
  assessmentsPage: (): string =>
    `${railsBaseUrl}/dashboard/trainer/assessments`,
  sequencesPage: (): string =>
    `${railsBaseUrl}/dashboard/trainer/assessments/sequences`,
  servicesPage: (): string => `${railsBaseUrl}${baseUrl}/embed/fbm/services`,
  servicesEdit: (serviceId: string | number): string =>
    `${railsBaseUrl}/fbm/services/${serviceId}`,

  clientProfile: (userId?: string | number): string =>
    `${railsBaseUrl}/users/${userId ?? ''}/`,
  clientManagerLogin: (clientId?: string | number): string =>
    `${railsBaseUrl}/users/manager_login/?id=${clientId ?? ''}`,
  managerLogout: `${railsBaseUrl}/users/manager_logout`,
  clientBooking: (clientId?: string | number): string =>
    `${railsBaseUrl}/a/booking?userId=${clientId ?? ''}`,
  booking: (trainerId?: string | number): string =>
    trainerId
      ? `${railsBaseUrl}/a/booking?staffId=${trainerId}`
      : `${railsBaseUrl}/a/booking`,
  clientCalendar: (clientId: string | number): string =>
    `${v3BaseUrl}/calendar/client/${clientId}/`,
  clientJournal: (
    clientId?: string | number,
    userId?: string | number,
    date?: string | number | Date,
    useNewJournal = true
  ): string => {
    if (!useNewJournal) {
      const dateUnix =
        date !== undefined ? getUnixTime(new Date(date)) : undefined;
      const userQuery = userId !== undefined ? `user_id=${userId}` : '';
      if (clientId) {
        const qs = [dateUnix !== undefined ? `date=${dateUnix}` : '', userQuery]
          .filter(Boolean)
          .join('&');
        return `${railsBaseUrl}/dashboard/journal/day${qs ? `?${qs}` : ''}`;
      }
      const qs = [dateUnix !== undefined ? `date=${dateUnix}` : '', userQuery]
        .filter(Boolean)
        .join('&');
      return `${railsBaseUrl}/dashboard/journal/${qs ? `?${qs}` : ''}`;
    }

    const datePath =
      date !== undefined ? `/${getUnixTime(new Date(date))}` : '';

    if (clientId) {
      const query = userId ? `?user_id=${userId}` : '';
      return `${baseUrl}/clients/${clientId}/journal${datePath}${query}`;
    }

    const query = userId ? `?user_id=${userId}` : '';
    return `${baseUrl}/me/journal${datePath}${query}`;
  },
  clientJournalWorkout: (
    userId?: string | number,
    workoutDate?: number | string | Date,
    clientId?: string | number,
    useNewJournal = true
  ): string => {
    if (!useNewJournal) {
      const dateUnix = workoutDate
        ? typeof workoutDate === 'number'
          ? workoutDate
          : getUnixTime(new Date(workoutDate))
        : undefined;
      const qs = [
        dateUnix !== undefined ? `date=${dateUnix}` : '',
        userId !== undefined ? `user_id=${userId}` : '',
      ]
        .filter(Boolean)
        .join('&');
      return `${railsBaseUrl}/dashboard/journal/day${qs ? `?${qs}` : ''}`;
    }

    let datePath = `/`;
    if (workoutDate) {
      const dateValue =
        typeof workoutDate === 'number'
          ? workoutDate
          : getUnixTime(new Date(workoutDate));
      datePath = `/${dateValue}`;
    }
    const query = userId ? `?user_id=${userId}` : '';

    if (clientId) {
      return `${baseUrl}/clients/${clientId}/journal${datePath}${query}`;
    }

    return `${baseUrl}/me/journal${datePath}${query}`;
  },

  appWorkoutPlan: (workoutPlanId: string | number): string =>
    `${v3BaseUrl}/app-webviews/workout-plans/${workoutPlanId}/`,
  clientMeasurements: (clientId?: string | number): string =>
    `${baseUrl}/clients/${clientId ?? ''}/health`,
  clientSnapshot: (
    clientId: string | number,
    userId: string | number
  ): string =>
    `${railsBaseUrl}/dashboard/snapshot/?client_id=${clientId}&user_id=${userId}`,
  clientPerformance: (
    clientId: string | number,
    userId: string | number
  ): string =>
    `${railsBaseUrl}/dashboard/performance/?client_id=${clientId}&user_id=${userId}`,
  journalPerformance: (
    exerciseId: string | number,
    startDate: number,
    endDate: number,
    clientId?: string | number
  ): string => {
    const basePath = clientId
      ? `${baseUrl}/clients/${clientId}/performance`
      : `${baseUrl}/me/performance`;
    const query = new URLSearchParams({
      performance_exerciseId: String(exerciseId),
      performance_startDate: String(startDate),
      performance_endDate: String(endDate),
    });
    return `${basePath}?${query.toString()}`;
  },

  manageClient: (clientId: string | number): string =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/info/`,
  manageClientPayment: (clientId: string | number): string =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/payment/`,
  manageClientPackages: (clientId: string | number): string =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/packages/`,
  manageClientDependents: (clientId: string | number): string =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/dependents/`,
  manageClientSignatures: (clientId: string | number): string =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/signatures/`,
  manageClientVisits: (clientId: string | number): string =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/visits/`,
  manageClientTrainingInfo: (clientId: string | number): string =>
    `${railsBaseUrl}/dashboard/trainer/clients/${clientId}/training/`,

  manageCustomMeasurements: (): string =>
    `${baseUrl}/settings/client#custom-client-measurements`,
  manageCustom: (): string => `${baseUrl}/settings/devices`,

  visitPaymentUrl: (
    visitId: string | number,
    useNewJournal = false
  ): string =>
    useNewJournal
      ? `${baseUrl}/me/journal/${getUnixTime(new Date())}?visit_id=${visitId}`
      : `${railsBaseUrl}/dashboard/journal/day?date=${getUnixTime(
          new Date()
        )}&visit_id=${visitId}`,
  visitProduct: (productId: string | number): string =>
    `${railsBaseUrl}/products/${productId}/`,
  visitProductFile: (
    productId: string | number,
    fileId: string | number
  ): string =>
    `${railsBaseUrl}/products/${productId}/download/?product_id=${fileId}`,

  adminLogin: (
    trainerId: string | number,
    host?: string,
    subdomain?: string
  ): string => {
    const path = `/users/${trainerId}/login`;
    let protocol: 'http' | 'https' = 'https';
    let hostVal = host;
    if (import.meta.env.MODE === 'development') {
      hostVal = hostVal ? `${hostVal}:3000` : hostVal;
      protocol = 'http';
    }
    if (hostVal) {
      return `${protocol}://${hostVal}${path}`;
    } else if (subdomain) {
      return `${protocol}://${subdomain}.exercise.com${path}`;
    } else {
      return `${railsBaseUrl}${path}`;
    }
  },

  legacyAdminEdit: (trainerId: string | number): string =>
    `${exerciseBaseUrl}/dashboard/users/edit/${trainerId}/?curTab=admin`,

  legacyModalAlertsNewModal: (): string =>
    `${v3BaseUrl}/admin/modal-alerts/new?alertType=modal`,
  legacyModalAlertsNewBanner: (): string =>
    `${v3BaseUrl}/admin/modal-alerts/new?alertType=banner`,
  legacyModalAlertEdit: (alertId: string | number): string =>
    `${v3BaseUrl}/admin/modal-alerts/${alertId}/edit`,

  printBarcodes: (userIds: string): string =>
    `${v3BaseUrl}/print-barcodes?chrome=false&clients=${userIds}`,

  report: (
    report: string,
    queryParams?: Record<string, string | number | boolean | undefined>
  ): string =>
    `${exerciseBaseUrl}/api/v4/reports/${report}.csv/${
      queryParams
        ? `?${Object.keys(queryParams)
            .map((key) => `${key}=${queryParams[key]}`)
            .join('&')}`
        : ''
    }`,

  reportUrl: (
    report: 'time_clock_entries',
    params: Partial<{
      start_date: number;
      start_date_string: string;
      end_date: number;
      end_date_string: string;
      time_interval: 'month';
      rangeType: 'week';
      page: number;
      per: number;
      trainer_id: string;
      task_id: string;
    }>
  ): string => {
    const urlParams = Object.keys(params)
      .map((key) => {
        const value = (params as any)[key];
        return `${key}=${value}`;
      })
      .join('&');
    return `${railsBaseUrl}/api/v4/reports/${report}.csv/?type=${report}&fetch_all=true&${urlParams}`;
  },

  contractSignatureUrl: (contractId: string | number): string =>
    `${railsBaseUrl}/a/contract/${contractId}/`,

  exportClientMeasurements: (
    clientIds: string,
    startDate?: string,
    endDate?: string,
    stats?: string
  ): string =>
    `${railsBaseUrl}/api/v2/clients/export_measurements?client_ids=${clientIds}${
      startDate ? `&start_date=${startDate}` : ''
    }${endDate ? `&end_date=${endDate}` : ''}${stats ? `&stats=${stats}` : ''}`,

  exportClientAccounts: (clientIds: string): string =>
    `${railsBaseUrl}/api/v2/clients/export_accounts?client_ids=${clientIds}`,

  receiptUrl: (
    userId: string | number,
    hashId: string,
    chargeId: string | number
  ): string =>
    `${railsBaseUrl}/receipts/${userId}/transaction_receipt.pdf/?receipt=${hashId}&charge=${chargeId}`,

  stripeCustomer: (userId?: string | number, platformUser = false): string =>
    `${exerciseBaseUrl}/users/${userId ?? ''}/stripe/${
      platformUser ? '' : '?stripe_customer_token=true'
    }`,

  editRoutine: (routineId: string | number): string =>
    `${v3BaseUrl}/routines/${routineId}/edit`,
  newRoutine: (): string => `${v3BaseUrl}/routines/new`,

  workoutPlanFinder: (): string => `${railsBaseUrl}/workout-plans/`,
  workoutPlanCalendar: (
    workoutPlanId?: string | number,
    clientId?: string | number
  ): string =>
    `${railsBaseUrl}/v2/workout-plan/${workoutPlanId}/calendar/${
      clientId ? `?client_id=${clientId}` : ''
    }`,
  planWorkoutCreator: (
    workoutPlanId: string | number,
    workoutId: string | number
  ): string => `${v3BaseUrl}/plans/${workoutPlanId}/workout/${workoutId}`,
  editPlanWorkout: (
    serviceId: string | number,
    workoutId: string | number
  ): string =>
    `${v3BaseUrl}/calendar/service/${serviceId}/workout/${workoutId}/`,
  serviceWorkoutCalendar: (serviceId: string | number): string =>
    `${v3BaseUrl}/calendar/service/${serviceId}/`,
  editWorkoutPlan: (workoutPlanId?: string | number): string =>
    `${v3BaseUrl}/plans/${workoutPlanId}/edit/`,
  viewWorkoutPlan: (workoutPlanId?: string | number): string =>
    `${railsBaseUrl}/workout-plans/${workoutPlanId}/`,

  viewGroupUrl: (cachedSlug: string): string =>
    `${baseUrl}/groups/${cachedSlug}/public`,
  newWorkoutPlan: (): string => `${railsBaseUrl}/v2/workout-plans/new/`,
  newWeekWorkoutPlan: (): string => `${railsBaseUrl}/a/plans/new/`,
  newFlexWorkoutPlan: (): string => `${railsBaseUrl}/a/plans/new/?type=flex`,
  createNewGroup: (): string => `${railsBaseUrl}/dashboard/groups/new`,
  createNewResource: (): string =>
    `${railsBaseUrl}/dashboard/trainer/resources/new`,
  /** Client-facing packages shop (replaces legacy v3 /a/packages purchase flow). */
  bookingPackages: (): string => `${baseUrl}/purchase/packages/`,
  purchasePackages: (
    packageId?: string | number,
    locationId?: string | number
  ): string => {
    const locationPrefix = locationId ? `/locations/${locationId}` : '';
    const itemPath = packageId ? `/packages/${packageId}` : '/packages';
    return makeAbsolute(`${baseUrl}/purchase${locationPrefix}${itemPath}`);
  },
  bookingHistory: (): string => `${baseUrl}/me/visits/`,

  viewReceipt: (
    userId: string | number,
    hashId: string,
    chargeId: string | number,
    stripeObjectId?: string
  ): string =>
    `${railsBaseUrl}/receipts/${userId}/transaction_receipt.pdf/?receipt=${hashId}&${
      chargeId ? `charge=${chargeId}` : `stripe_id=${stripeObjectId}`
    }`,

  cancelSubscription: (
    sub: { subId: string },
    currentUser: { authToken: string }
  ): string =>
    `${railsBaseUrl}/leaving/?subscription_id=${sub.subId}?request_from_app=true&auth_token=${currentUser.authToken}`,
  cancelAllSubscriptions: (): string => `${railsBaseUrl}/leaving/`,
  reactivateSubscription: (sub: { subId: string }): string =>
    `${railsBaseUrl}/reupgrade/?subscription_id=${sub.subId}`,

  security: (): string => `${railsBaseUrl}/embed/dashboard/me/security/`,
  /** Join URL; use the group's numeric database id (not cachedSlug) for reliable server lookups. */
  groupJoin: (groupId?: string | number): string =>
    `${railsBaseUrl}/groups/${groupId ?? ''}/join/`,
  stripeSubscriptions: (): string => `${baseUrl}/stripe/payment-plans/`,
  manageLocations: (): string => `${baseUrl}/fbm/locations`,
  addGroupVideo: (groupId: string | number): string =>
    `${baseUrl}/groups/${groupId}/videos/new`,
  editGroupVideos: (
    groupId: string | number,
    videoId: string | number
  ): string => `${baseUrl}/groups/${groupId}/videos/${videoId}`,
  addGroupResource: (groupId: string | number): string =>
    `${baseUrl}/groups/${groupId}/resources/new`,
  editGroupResources: (
    groupId: string | number,
    resourceId: string | number
  ): string => `${baseUrl}/groups/${groupId}/resources/${resourceId}`,
  addGroupAssessments: (groupId: string | number): string =>
    `${baseUrl}/groups/${groupId}/assessments/new`,
  editGroupAssessments: (
    groupId: string | number,
    assessmentId: string | number
  ): string => `${baseUrl}/groups/${groupId}/assessments/${assessmentId}`,
  editGroupAssignment: (assessmentId: string | number): string =>
    `${baseUrl}/assessments/${assessmentId}`,
  viewProduct: (productId: string | number): string =>
    `${baseUrl}/products/${productId}`,
  newTrainer: (): string => `${baseUrl}/trainers/new`,
  editTrainer: (trainerId: string | number, tab = 'profile'): string =>
    `${baseUrl}/trainers/${trainerId}/${tab}`,
  cloneTrainer: (trainerId: string | number): string =>
    `${baseUrl}/trainers/new?copiedFromTrainerId=${trainerId}`,
  clients: (trainerId?: string | number): string =>
    `${baseUrl}/clients/?${trainerId ? `subTrainerId=${trainerId}` : ''}`,
  connectionsUrl: (): string => `${baseUrl}/connections?nochrome=true`,
  contact: (urlBase?: string): string => `${urlBase || railsBaseUrl}/contact`,
  signup: (urlBase?: string): string =>
    `${urlBase || railsBaseUrl}/users/sign_up`,
  fbmPackage: (purchaseId: string | number, userId?: string | number): string =>
    `${railsBaseUrl}/packages/${purchaseId}/purchase/${userId ? `?t=${userId}` : ''}`,
  fbmPackageLocation: (
    id: string | number,
    locationId: string | number
  ): string => `/packages/${id}/purchase/?location_id=${locationId}`,
  managePackages: (): string => `${railsBaseUrl}/fbm/packages`,
  publicPackages: (): string => `${baseUrl}/purchase/packages/`,
  customizePlatformClientSettings: (): string => `${baseUrl}/settings/client`,
  workoutPlan: (purchaseId: string | number, userId: string | number): string =>
    `${railsBaseUrl}/workout-plans/${purchaseId}/purchase/${userId ? `?t=${userId}` : ''}`,
  exercisecomProduct: (
    purchaseId: string | number,
    userId: string | number
  ): string => `/products/${purchaseId}/purchase/?t=${userId}`,
  group: (purchaseId: string | number, userId: string | number): string =>
    `${railsBaseUrl}/groups/${purchaseId}/join/${userId ? `?t=${userId}` : ''}`,
  clientAssociation: (
    trainerId: string | number,
    userId: string | number
  ): string => `/trainers/${trainerId}/join/?t=${userId}`,
  trainerAssociation: (userId: string | number): string =>
    `/trainers/staff-join/?t=${userId}`,
  productPurchase: (
    productId: string | number,
    variantId?: string | number,
    locationId?: string | number
  ): string => {
    const url = `/products/${productId}/purchase${
      variantId ? `?variant=${variantId}` : ''
    }${locationId ? `&location=${locationId}` : ''}`;
    return `${railsBaseUrl}${url}`;
  },

  oauthConnectUrl: ({
    appId,
    currentUser,
    localDomain = true,
  }: {
    appId: string | number;
    currentUser: { authToken?: string } | { authToken: string } | any;
    localDomain?: boolean;
  }): string =>
    `${
      localDomain ? railsBaseUrl : ngrokUrl
    }/users/start_oauth/${appId}?auth_token=${currentUser.authToken}`,
  localConnectUrl: ({
    appId,
    currentUser,
    localDomain = true,
    meta,
  }: {
    appId: string | number;
    currentUser: { authToken?: string } | { authToken: string } | any;
    localDomain?: boolean;
    meta?: string;
  }): string =>
    `${
      localDomain ? railsBaseUrl : ngrokUrl
    }/users/start_auth/${appId}?auth_token=${currentUser.authToken}${
      meta ? `&${meta}` : ''
    }`,
  viewVideo: (videoId: string | number): string =>
    `${railsBaseUrl}/a/app-webviews/videos/video/${videoId}`,
  aBooking: (domain?: string): string => `${domain || railsBaseUrl}/a/booking?`,
  kioskCheckIn: (
    domain?: string,
    allowCheckIn?: string | number,
    checkInLocation?: string
  ): string =>
    `${domain || railsBaseUrl}/ex4/check-in/${allowCheckIn}${
      checkInLocation !== '-1' ? `?location_id=${checkInLocation}` : ''
    }`,
  barcodeCheckIn: (domain?: string, barcodeLocation?: string): string =>
    `${domain || railsBaseUrl}/ex4/checkin/${
      barcodeLocation !== '-1' ? `?location=${barcodeLocation}` : ''
    }`,
  trainerJoinUrl: (domain: string, cachedSlug: string): string =>
    `${domain}/trainers/${cachedSlug}/join`,
  leads: (): string => `${railsBaseUrl}/dashboard/trainer/assessments/leads`,
  calendar: (id: string | number): string =>
    `${railsBaseUrl}/a/calendar/service/${id}`,
  chargeBehavior:
    'https://www.exercise.com/support/service-balances/#step-7-select-the-subscription-charge-behavior',
  serviceBalance: 'https://www.exercise.com/support/service-balances/',
  visitsResetBillingCycle:
    'https://www.exercise.com/support/reset-billing-cycle-or-recharge-package/',
  businessSales: (id: string | number, type: string): string =>
    `${railsBaseUrl}/dashboard/trainer/business/sales/?purchaseableId=${id}&purchaseableType=${type}`,
  businessSubscriptions: (id: string | number, type: string): string =>
    `${railsBaseUrl}/dashboard/trainer/business/subscriptions/?saleItem=${id}_${type}`,
  workoutPlans: (slug?: string | number): string =>
    `${railsBaseUrl}/workout-plans/${slug ?? ''}/`,
  workoutPlansFollowUrl: (slug?: string | number): string =>
    `${railsBaseUrl}/workout-plans/${slug ?? ''}/#modal-follow`,
  workoutPlansEditUrl: (
    plan?: { id: string | number; slug?: string },
    usesNewCreator?: boolean
  ): string =>
    usesNewCreator
      ? `${railsBaseUrl}/a/plans/${plan?.id ?? ''}/edit/`
      : `${railsBaseUrl}/v2/workout-plan/${plan?.slug ?? ''}/calendar/`,
  journalUrl: (useNewJournal: boolean): string =>
    useNewJournal
      ? `${baseUrl}/me/journal`
      : `${railsBaseUrl}/dashboard/journal/`,
  workoutPlanPurchaseLink: (
    plan: { id: string | number },
    base: string = railsBaseUrl
  ): string => `${base}/workout-plans/${plan.id}/purchase`,
  newClient: (trainerId?: string | number): string =>
    `${railsBaseUrl}/dashboard/trainer/clients/new${
      trainerId ? `?subTrainerId=${trainerId}` : ''
    }`,
  assessmentKiosk :(assessmentId: string | number, clientModelId: string | number)=>
    `${railsBaseUrl}/assessment/${assessmentId}/?userId=${clientModelId}&kioskMode=true&chrome=false`,
  // Force exercise.com domain, as there's a redirect to the TOS PDF set only for the production page
  termsOfService: `https://www.exercise.com/terms-of-service/`,
  webviewPlans: (id) => `${railsBaseUrl}/a/app-webviews/workout-plans/${id}/`,

  contactUs: () => `${railsBaseUrl}/a/app-webviews/contact`,

  editWorkout: (id: string) =>
    `${railsBaseUrl}/ex4/embed/v2/workout/${id}/edit/`,

  editEventWorkoutPlan: (workoutPlanId: string, objectId: string) =>
    `${railsBaseUrl}/ex4/embed/v2/workout-plan/${workoutPlanId}/workout/${objectId}`,
} as const;
